import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import {
    TitleComponent,
    TooltipComponent,
    GridComponent,
    LegendComponent
} from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    LegendComponent,
    BarChart,
    CanvasRenderer
]);

export default function InstockOutstockChart({ stock = [] }) {
    const chartRef = useRef(null);

    useEffect(() => {
        if (!stock.length) return;

        const chartInstance = echarts.init(chartRef.current);

        // Prepare data for the chart
        const months = stock.map(item => item.month_name);
        const stockInData = stock.map(item => parseFloat(item.stock_in_total) || 0);
        const stockOutData = stock.map(item => parseFloat(item.stock_out_total) || 0);

        const option = {
            title: {
                text: 'Stock In vs Stock Out Analysis',
                left: 'center',
                textStyle: {
                    fontSize: 16,
                    fontWeight: 'bold'
                },
                padding: [10, 0, 20, 0]
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow'
                },
                formatter: function(params) {
                    const stockIn = params[0];
                    const stockOut = params[1];
                    const month = stock[params[0].dataIndex].month_name;
                    return `
                        <div style="font-weight:bold;margin-bottom:5px">${month}</div>
                        <div style="display:flex;justify-content:space-between">
                            <span>${stockIn.seriesName}:</span>
                            <span style="margin-left:10px;font-weight:bold">${stockIn.value}</span>
                        </div>
                        <div style="display:flex;justify-content:space-between">
                            <span>${stockOut.seriesName}:</span>
                            <span style="margin-left:10px;font-weight:bold">${stockOut.value}</span>
                        </div>
                        <div style="display:flex;justify-content:space-between;margin-top:5px">
                            <span>Net Change:</span>
                            <span style="margin-left:10px;font-weight:bold">${parseFloat(stock[params[0].dataIndex].net_stock_change) || 0}</span>
                        </div>
                    `;
                }
            },
            legend: {
                data: ['Stock In', 'Stock Out'],
                top: 40,
                right: 20
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: true,
                top: 80
            },
            xAxis: {
                type: 'value',
                axisLabel: {
                    color: '#6c757d'
                },
                splitLine: {
                    lineStyle: {
                        color: '#e9ecef'
                    }
                }
            },
            yAxis: {
                type: 'category',
                data: months,
                axisLabel: {
                    color: '#6c757d'
                },
                axisLine: {
                    show: false
                },
                axisTick: {
                    show: false
                }
            },
            series: [
                {
                    name: 'Stock In',
                    type: 'bar',
                    stack: 'total',
                    label: {
                        show: false
                    },
                    emphasis: {
                        focus: 'series'
                    },
                    itemStyle: {
                        color: '#3b82f6' // blue-500 equivalent
                    },
                    data: stockInData
                },
                {
                    name: 'Stock Out',
                    type: 'bar',
                    stack: 'total',
                    label: {
                        show: false
                    },
                    emphasis: {
                        focus: 'series'
                    },
                    itemStyle: {
                        color: '#ec4899' // pink-500 equivalent
                    },
                    data: stockOutData
                }
            ]
        };

        chartInstance.setOption(option);

        const handleResize = () => {
            chartInstance.resize();
        };
        window.addEventListener('resize', handleResize);

        return () => {
            chartInstance.dispose();
            window.removeEventListener('resize', handleResize);
        };
    }, [stock]);

    return (
        <div className="card shadow-sm">
            <div className="card-body p-3">
                {stock.length > 0 ? (
                    <div ref={chartRef} style={{ width: '100%', height: '400px' }}></div>
                ) : (
                    <div className="text-center py-5">
                        <p className="text-muted">No stock data available</p>
                    </div>
                )}
            </div>
        </div>
    );
}